<template>
    <div class="text-center">
        <!-- Pick / capture -->
        <template v-if="!imageSrc">
            <div class="cropper-drop" @click="pick">
                <v-icon size="40" color="primary">mdi-camera-plus-outline</v-icon>
                <p class="text-body-2 text-medium-emphasis mt-2 mb-0">Take a photo or upload one</p>
            </div>
        </template>

        <!-- Crop + zoom -->
        <template v-else>
            <div ref="viewport" class="cropper-viewport"
                 @mousedown="startDrag" @touchstart.prevent="startDrag">
                <img :src="imageSrc" :style="imgStyle" alt="crop" draggable="false"/>
                <div class="cropper-ring"/>
            </div>
            <div class="d-flex align-center ga-2 px-2 mt-3">
                <v-icon size="18" color="grey">mdi-magnify-minus-outline</v-icon>
                <v-slider v-model="zoom" :min="1" :max="3" :step="0.01" hide-details density="compact" color="primary"/>
                <v-icon size="18" color="grey">mdi-magnify-plus-outline</v-icon>
            </div>
            <div class="d-flex justify-center ga-2 mt-2">
                <v-btn variant="text" size="small" prepend-icon="mdi-image-refresh-outline" @click="pick">Change</v-btn>
                <v-btn color="primary" variant="flat" size="small" prepend-icon="mdi-check" @click="apply">Use photo</v-btn>
            </div>
        </template>

        <!-- No capture attr so mobile offers both Camera and Photo Library. -->
        <input ref="fileInput" type="file" accept="image/*" class="d-none" @change="onFile"/>
    </div>
</template>

<script setup>
import {computed, reactive, ref} from "vue";

const emit = defineEmits(['cropped']);
const props = defineProps({
    viewportSize: {type: Number, default: 240},
    output: {type: Number, default: 400},
});

const fileInput = ref(null);
const viewport = ref(null);
const imageSrc = ref("");
const zoom = ref(1);
const offset = reactive({x: 0, y: 0});
const nat = reactive({w: 0, h: 0}); // natural image size

const pick = () => fileInput.value?.click();

const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
        const img = new Image();
        img.onload = () => {
            nat.w = img.naturalWidth;
            nat.h = img.naturalHeight;
            zoom.value = 1;
            offset.x = 0;
            offset.y = 0;
            imageSrc.value = reader.result;
        };
        img.src = reader.result;
    };
    reader.readAsDataURL(file);
};

// Base scale makes the shorter side cover the circular viewport; zoom multiplies.
const baseScale = computed(() => {
    const min = Math.min(nat.w, nat.h) || 1;
    return props.viewportSize / min;
});
const effScale = computed(() => baseScale.value * zoom.value);
const drawnW = computed(() => nat.w * effScale.value);
const drawnH = computed(() => nat.h * effScale.value);
const left = computed(() => (props.viewportSize - drawnW.value) / 2 + offset.x);
const top = computed(() => (props.viewportSize - drawnH.value) / 2 + offset.y);

const imgStyle = computed(() => ({
    position: "absolute",
    width: `${drawnW.value}px`,
    height: `${drawnH.value}px`,
    left: `${left.value}px`,
    top: `${top.value}px`,
    userSelect: "none",
    pointerEvents: "none",
}));

// Drag to pan (mouse + touch).
let start = null;
const startDrag = (e) => {
    const p = e.touches ? e.touches[0] : e;
    start = {x: p.clientX - offset.x, y: p.clientY - offset.y};
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("touchmove", onDrag, {passive: false});
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchend", endDrag);
};
const onDrag = (e) => {
    if (!start) return;
    if (e.cancelable) e.preventDefault();
    const p = e.touches ? e.touches[0] : e;
    offset.x = p.clientX - start.x;
    offset.y = p.clientY - start.y;
};
const endDrag = () => {
    start = null;
    window.removeEventListener("mousemove", onDrag);
    window.removeEventListener("touchmove", onDrag);
    window.removeEventListener("mouseup", endDrag);
    window.removeEventListener("touchend", endDrag);
};

// Render the visible circle to a square canvas using the same transform.
const apply = () => {
    const O = props.output;
    const scaleOut = O / props.viewportSize;
    const canvas = document.createElement("canvas");
    canvas.width = O;
    canvas.height = O;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
        ctx.drawImage(
            img,
            left.value * scaleOut,
            top.value * scaleOut,
            drawnW.value * scaleOut,
            drawnH.value * scaleOut,
        );
        canvas.toBlob((blob) => {
            if (blob) {
                emit('cropped', new File([blob], 'avatar.jpg', {type: 'image/jpeg'}));
            }
        }, 'image/jpeg', 0.9);
    };
    img.src = imageSrc.value;
};

defineExpose({pick, hasImage: computed(() => !!imageSrc.value)});
</script>

<style scoped>
.cropper-drop {
    border: 2px dashed rgba(var(--v-theme-primary), 0.4);
    border-radius: 16px;
    padding: 28px 16px;
    cursor: pointer;
    transition: background 0.2s ease;
}

.cropper-drop:hover {
    background: rgba(var(--v-theme-primary), 0.06);
}

.cropper-viewport {
    position: relative;
    width: 240px;
    height: 240px;
    margin: 0 auto;
    border-radius: 50%;
    overflow: hidden;
    background: #000;
    cursor: grab;
    touch-action: none;
}

.cropper-viewport:active {
    cursor: grabbing;
}

.cropper-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.7) inset;
    pointer-events: none;
}
</style>
